import {
  AvRefreshReasons,
  DeviceErrors,
  avSelectors,
  useAvContext,
} from "@minervaproject/avsubsystem";
import {
  BreakoutsFilledIcon,
  Button,
  CameraIcon,
  CameraOffIcon,
  CaptionsIcon,
  CheckIcon,
  MicMutedIcon,
  MicUnmutedIcon,
  RefreshIcon,
  RefreshVideoIcon,
  SoundHighIcon,
  palette,
} from "@minervaproject/fds";
import { ButtonKinds } from "@minervaproject/fds/es/components/button";
import clsx from "clsx";

import { AvIssueIcon } from "components/av/av_issue_icon";
import { useCapabilities, useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import {
  sendPubsubMessage,
  setPttModeForUser,
  setUserEnableLiveCaptions,
  visitBreakoutRoom,
} from "shared/reducers/classroom";
import { CapabilityType, PageUnloadReasonType, PubsubMessageTypeType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { BandwidthModeType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store/index";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { selectEffectivePttMode } from "store/selectors/classroom";

import "./participant_info_menu.scss";

function ParticipantInfoMenu({
  userId,
  index,
  isVertical = false,
  transformTopRight = false,
  isDirectory = false,
}: {
  userId: string;
  index?: number;
  isVertical?: boolean;
  transformTopRight?: boolean;
  isDirectory?: boolean;
}) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const targetUser = useUserProperties(userId, [
    "bandwidth_mode",
    "enable_live_captions",
    "is_speaking",
    "pronunciation_url",
  ]);

  const targetPttMode = useAppSelector(selectEffectivePttMode(userId));
  const {
    canAccessMonitoringDashboard,
    canChangeBandwidthMode,
    canControlClassroom,
    canRefreshAv,
    canRepublishStreams,
    canVisitBreakouts,
  } = useCapabilities();
  const targetUserIsVisibleInClass = useCapability(CapabilityType.IS_VISIBLE_IN_CLASS, { userId });
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const cameraError = useAppSelector(classroomSelectors.selectUserCameraError(userId));
  const micError = useAppSelector(classroomSelectors.selectUserMicError(userId));
  const avAvailable = useAppSelector(classroomSelectors.selectUserHasAvailableAv(userId));
  const poorConnection = useAppSelector(classroomSelectors.selectUserHasPoorConnection(userId));
  const streamState = avSelectors.useStreamState(userId);
  const avConnection = useAvContext();

  const isSelf = currentUserId === userId;
  const hasSupportCapabilities = canChangeBandwidthMode && canRefreshAv && canRepublishStreams;

  const avIssue = !!micError || !avAvailable;
  const avNotRevivable = !targetUserIsVisibleInClass || avIssue;
  const avInexplicablyNotAvailable = !avAvailable && !!micError;

  const micBlocked = micError === DeviceErrors.AUDIO_NOT_ALLOWED;
  const noMic = micError === DeviceErrors.AUDIO_NOT_FOUND;
  const mutedAudio = targetPttMode && targetUser?.is_speaking === false;
  const audioOnlyBandwidthMode =
    targetUser?.bandwidth_mode === BandwidthModeType.AudioOnly ||
    targetUser?.bandwidth_mode === BandwidthModeType.AudioOnlyForced;

  const cameraBlocked = cameraError === DeviceErrors.VIDEO_NOT_ALLOWED;
  const noCamera = cameraError === DeviceErrors.VIDEO_NOT_FOUND;
  const mutedVideo = !streamState?.video_enabled || !streamState?.video_available;

  const pronunciationAudio =
    targetUser?.pronunciation_url && new Audio(targetUser.pronunciation_url);

  const targetUserBreakoutRoom = useAppSelector(
    classroomSelectors.selectAssignedBreakoutRoomByUserId(userId),
  );
  const currentUserBreakoutRoom = useAppSelector(
    classroomSelectors.selectActiveBreakoutRoomForCurrentUser(),
  );
  const breakoutIsActiveAndNotInSameRoom =
    !!targetUserBreakoutRoom && targetUserBreakoutRoom.id !== currentUserBreakoutRoom?.id;

  const iconProps = {
    width: "12px",
    height: "12px",
    color: palette.blackTint90,
  };

  const handleReloadAv = () => {
    const reason = isSelf ? AvRefreshReasons.USER_REQUESTED : AvRefreshReasons.ADMIN_REQUESTED;
    if (isSelf) {
      avConnection?.refreshAv(reason);
    } else {
      dispatch(
        sendPubsubMessage({
          type: PubsubMessageTypeType.REFRESH_AV,
          data: { reason, targetUserId: userId },
        }),
      );
    }

    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.ADMIN_FORCE_RELOAD_AV,
        userId: currentUserId,
        contextData: { targetUserId: userId },
      }),
    );
  };

  const handleReloadPage = () => {
    if (isSelf) {
      window.pageUnloadReason = PageUnloadReasonType.FORCE_RELOAD_PAGE_INDIVIDUAL;
      location.reload();
    } else {
      dispatch(
        sendPubsubMessage({
          type: PubsubMessageTypeType.RELOAD_PAGE,
          data: { reason: PageUnloadReasonType.FORCE_RELOAD_PAGE_INDIVIDUAL, targetUserId: userId },
        }),
      );
    }
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.ADMIN_FORCE_RELOAD_PAGE,
        userId: currentUserId,
        contextData: { targetUserId: userId },
      }),
    );
  };

  const handleReloadStream = () => {
    avConnection?.reloadStream(userId);
  };

  const handleForceMute = () => {
    dispatch(setPttModeForUser({ userId, pttMode: true }));
  };

  const handleForceToHighBandwidth = () => {
    dispatch(
      sendPubsubMessage({
        type: PubsubMessageTypeType.FORCE_BANDWIDTH_MODE_CHANGE,
        data: { bandwidthMode: BandwidthModeType.AutomaticBandwidth, targetUserId: userId },
      }),
    );
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.ADMIN_FORCE_HIGH_BANDWIDTH,
        userId: currentUserId,
        contextData: { targetUserId: userId },
      }),
    );
  };

  const handleForceToAudioOnly = () => {
    dispatch(
      sendPubsubMessage({
        type: PubsubMessageTypeType.FORCE_BANDWIDTH_MODE_CHANGE,
        data: { bandwidthMode: BandwidthModeType.AudioOnlyForced, targetUserId: userId },
      }),
    );
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.ADMIN_FORCE_AUDIO_ONLY,
        userId: currentUserId,
        contextData: { targetUserId: userId },
      }),
    );
  };

  const handleVisitBreakoutRoom = () => {
    if (targetUserBreakoutRoom) {
      dispatch(
        visitBreakoutRoom({ userId: currentUserId, breakoutRoomId: targetUserBreakoutRoom.id }),
      );
    }
  };

  const handleForceLiveCaptionChange = () => {
    if (!targetUser) return;
    const enableLiveCaptions = !targetUser.enable_live_captions;
    dispatch(setUserEnableLiveCaptions({ userId: targetUser.id, enableLiveCaptions }));
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.ADMIN_FORCE_LIVE_CAPTIONS_CHANGE,
        userId: currentUserId,
        contextData: { targetUserId: targetUser.id, enableLiveCaptions },
      }),
    );
  };

  if (!targetUser) return null;

  return (
    <div
      className={clsx(
        "absolute bg-black-tint-20 body-xs text-black-tint-90 pt2 px2 pb1 border-box shadow-level-3 pim",
        {
          ml1: isVertical,
          mt1: !isVertical,
        },
      )}
      style={
        isVertical
          ? {
              top: index === 0 ? 0 : "unset",
              bottom: index !== 0 ? 0 : "unset",
              left: "267px",
              width: "267px",
            }
          : isDirectory
            ? {
                width: "272px",
                top: "calc(100% - 4px)",
                left: 0,
                zIndex: 10,
              }
            : {
                top: "calc(100% * var(--participant-scale))",
                right: transformTopRight ? "0" : "unset",
                width: "267px",
              }
      }
      data-testid={`pim-${userId}`}
    >
      {/* RELOAD BUTTONS */}
      <div className="mb1">
        {hasSupportCapabilities ? (
          <div className="flex mb1">
            <Button
              className="mr2"
              kind={ButtonKinds.ClassroomSecondary}
              onClick={handleReloadAv}
              data-testid="refresh-av"
            >
              <RefreshVideoIcon color="var(--black-tint-90)" />
              Reload A/V
            </Button>
            <Button
              kind={ButtonKinds.ClassroomSecondary}
              onClick={handleReloadPage}
              data-testid="reload-page"
            >
              <RefreshIcon color="var(--black-tint-90)" />
              Reload Page
            </Button>
          </div>
        ) : avNotRevivable ? (
          <Button kind={ButtonKinds.ClassroomSecondary} disabled>
            <RefreshVideoIcon color="var(--black-tint-90)" />
            A/V Not Available
          </Button>
        ) : (
          <Button
            kind={ButtonKinds.ClassroomSecondary}
            onClick={handleReloadStream}
            data-testid="reload-stream"
          >
            <RefreshVideoIcon color="var(--black-tint-90)" ariaLabel="reload-stream" />
            Reload
          </Button>
        )}
      </div>

      {/* CONNECTION STATUS */}
      <div className="flex items-center p1">
        {poorConnection ? (
          <>
            <AvIssueIcon poorConnection title="Connection Issue" displayTitle size={12} />
            {canAccessMonitoringDashboard && (
              <a
                href={`/monitoring/classes/${classInfo.id}/users/${userId}`}
                target="_blank"
                className="text-button ml2"
                rel="noreferrer"
              >
                Details
              </a>
            )}
            {isSelf && (
              <a
                href="https://help.minervaproject.com/en/articles/4296298-how-to-get-the-fastest-internet-connection"
                target="_blank"
                className="text-button ml2"
                rel="noreferrer"
              >
                Learn More
              </a>
            )}
          </>
        ) : (
          <>
            <CheckIcon {...iconProps} />
            <span className="ml2">No Connection Issues</span>
          </>
        )}
      </div>

      {/* MIC STATUS */}
      <div
        className="flex items-center p1"
        style={
          avInexplicablyNotAvailable || micBlocked || noMic
            ? {
                backgroundColor: "var(--red-shade-20)",
                borderRadius: "3px",
              }
            : {}
        }
      >
        {!targetUserIsVisibleInClass ? (
          <>
            <MicMutedIcon {...iconProps} />
            <span className="ml2">Mic Not Required</span>
          </>
        ) : micBlocked ? (
          <AvIssueIcon title="Mic Blocked" displayTitle size={12} />
        ) : noMic ? (
          <AvIssueIcon title="No Mic Detected" displayTitle size={12} />
        ) : avInexplicablyNotAvailable ? (
          <AvIssueIcon title="No Audio Signal" displayTitle size={12} />
        ) : !mutedAudio ? (
          <>
            <MicUnmutedIcon {...iconProps} />
            <span className="ml2">Mic Unmuted</span>
            {canControlClassroom && (
              <button type="button" className="text-button ml2" onClick={handleForceMute}>
                Mute
              </button>
            )}
          </>
        ) : (
          <>
            <MicMutedIcon {...iconProps} />
            <span className="ml2">Mic Muted</span>
          </>
        )}
      </div>

      {/* CAMERA STATUS */}
      <div className="flex items-center p1">
        {!audioOnlyBandwidthMode && avInexplicablyNotAvailable ? (
          <AvIssueIcon title="No Video Signal" displayTitle size={12} />
        ) : audioOnlyBandwidthMode ? (
          <>
            <AvIssueIcon title="Audio Only" displayTitle size={12} />
            {canControlClassroom && (
              <button
                type="button"
                className="text-button ml2"
                onClick={handleForceToHighBandwidth}
              >
                Enable Video
              </button>
            )}
          </>
        ) : !targetUserIsVisibleInClass ? (
          <>
            <CameraOffIcon {...iconProps} />
            <span className="ml2">Camera Not Required</span>
          </>
        ) : cameraBlocked ? (
          <AvIssueIcon title="Camera Blocked" displayTitle size={12} />
        ) : noCamera ? (
          <AvIssueIcon title="No Camera Detected" displayTitle size={12} />
        ) : micError ? (
          <AvIssueIcon title="No Video Signal" displayTitle size={12} />
        ) : !mutedVideo ? (
          <>
            <CameraIcon {...iconProps} />
            <span className="ml2">Camera Enabled</span>
          </>
        ) : (
          <>
            <CameraOffIcon {...iconProps} />
            <span className="ml2">Camera Disabled</span>
          </>
        )}
        {!audioOnlyBandwidthMode && canControlClassroom && !micError && (
          <button type="button" className="text-button ml2" onClick={handleForceToAudioOnly}>
            Make Audio Only
          </button>
        )}
      </div>

      {/* LIVE CAPTIONS STATUS */}
      {canControlClassroom && (
        <div className="flex items-center p1">
          <CaptionsIcon
            {...iconProps}
            color={targetUser.enable_live_captions ? palette.green : null}
            outlineColor={targetUser.enable_live_captions ? palette.green : palette.blackTint90}
            strokeColor={targetUser.enable_live_captions ? palette.black : palette.blackTint90}
          />
          <span className="ml2">{`Captioning ${
            targetUser.enable_live_captions ? "Enabled" : "Disabled"
          }`}</span>
          <button type="button" className="text-button ml2" onClick={handleForceLiveCaptionChange}>
            {targetUser.enable_live_captions ? "Disable" : "Enable"}
          </button>
        </div>
      )}

      {/* NAME PRONUNCIATION */}
      {pronunciationAudio && (
        <div className="flex items-center p1">
          <SoundHighIcon {...iconProps} />
          <button
            type="button"
            className="text-button ml2"
            onClick={() => pronunciationAudio.play()}
          >
            Pronounce Name
          </button>
        </div>
      )}

      {/* BREAKOUT GROUP */}
      {canVisitBreakouts && breakoutIsActiveAndNotInSameRoom && (
        <div className="flex items-center p1">
          <BreakoutsFilledIcon {...iconProps} />
          <span className="ml2">In Breakout Group {targetUserBreakoutRoom.title}</span>
          <button type="button" className="text-button ml2" onClick={handleVisitBreakoutRoom}>
            Visit
          </button>
        </div>
      )}
    </div>
  );
}

export { ParticipantInfoMenu };
